import { MagnifyImage } from "@/components/MagnifyImage";
import { HeaderText, Skeleton } from "@/components/ui";
import { useS3Download } from "@/network/useS3Download";
import { useParams, useSearch } from "@tanstack/react-router";
import { type RefObject, useEffect, useRef, useState } from "react";
import { Document, Page } from "react-pdf";
import { useGetEncounterFiles } from "../../network/useGetEncounterFiles";

interface FileItemProps {
  patientId: string;
  filePath: string;
  magnifyImageOffsetRef?: RefObject<Element | null>;
}

const FileItem = ({
  patientId,
  filePath,
  magnifyImageOffsetRef,
}: FileItemProps) => {
  const [src, setSrc] = useState<string | null>(null);
  const { data, isLoading } = useS3Download({
    patientId,
    path: filePath,
    enabled: Boolean(filePath),
  });

  useEffect(() => {
    if (data) {
      const objectUrl = URL.createObjectURL(data);
      setSrc(objectUrl);
      return () => {
        URL.revokeObjectURL(objectUrl);
      };
    }
  }, [data]);

  if (isLoading || !data || !src)
    return (
      <div>
        <Skeleton className="aspect-square w-full mt-2" />
      </div>
    );

  return (
    <>
      {data.type !== "application/pdf" ? (
        <MagnifyImage
          className="bg-secondary rounded-lg object-contain aspect-square"
          src={src}
          magnifyImageOffsetRef={magnifyImageOffsetRef}
        />
      ) : (
        <a href={src} target="_blank" rel="noreferrer">
          <Document
            file={src}
            className="aspect-square bg-tertiary rounded-lg flex items-center justify-center overflow-hidden"
            loading={<Skeleton className="aspect-square" />}
          >
            <Page
              className="w-fit h-fit"
              height={100}
              pageIndex={0}
              renderTextLayer={false}
              renderAnnotationLayer={false}
            />
          </Document>
        </a>
      )}
    </>
  );
};

export const ObjectiveFiles = () => {
  const { patientId } = useParams({ from: "/patient/$patientId" });
  const { encounterId } = useSearch({ from: "/patient/$patientId" });
  const containerRef = useRef<HTMLDivElement>(null);
  const { data: encounterFiles } = useGetEncounterFiles({
    patientId,
    motherEncounterId: encounterId as string,
  });

  if (!encounterFiles?.files?.length) {
    return null;
  }

  return (
    <div ref={containerRef} className="flex flex-col">
      <HeaderText size="sm">Files</HeaderText>
      <div className="mt-3 grid lg:grid-cols-3 xl:grid-cols-4 gap-3 overflow-auto pb-3 h-fit">
        {encounterFiles.files.map((file) => (
          <FileItem
            key={file.documentReferenceId}
            patientId={patientId}
            filePath={file.fileUrl}
            magnifyImageOffsetRef={containerRef}
          />
        ))}
      </div>
    </div>
  );
};
